import React, { useState, useEffect, useRef, useMemo } from 'react';
import useParseJSONStore, { getParseJSONStore } from './useParseJSONStore';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import './ParseJSON.css';

import { getCurrentTime } from '../../utils/getCurrentTime';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';

import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import { Button, CircularProgress, Grid, TextField } from '@mui/material';
import CustomPaper from '../common/CustomPaper';
import ReactJson from 'react-json-view';
import ErrorIcon from '@mui/icons-material/Error';
import ChipInput from '../ChipInput/ChipInput';
import PlaceholderJson from '../Component/PlaceholderJson';
import handleJsonFunction from '../Component/HandleJsonFunction';
import RefreshIcon from '@mui/icons-material/Refresh';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import { addCompleter } from 'ace-builds/src-noconflict/ext-language_tools';
import { styled } from '@mui/material/styles';

const StyledGrid = styled(Grid)(({}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
}));
const PrettifyButton = styled(Button)(({}) => ({
  background: 'black',
  color: 'white',
  '&:hover': {
    background: '#333',
  },
}));

const ParseJSONTemplate = (props) => {
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingSample, setUploadingSample] = useState(false);
  const [error, setError] = useState(false);
  const [jsonParsed, setJsonParsed] = useState(false);
  const [status, setStatus] = useState(null);
  const [focus, _setFocus] = useState(null);
  const focusRef = useRef(null);
  const setFocus = (val) => {
    _setFocus(val);
    focusRef.current = val;
  };

  const store = useMemo(() => getParseJSONStore(props.component_id), [props.component_id]);
  const componentData = store(s => s.data);
  const componentName = useProjectStore(s => s.componentName);

  useEffect(() => {
    if (props.component_id == undefined) return;

    // If store was prefilled from canvas load, skip the REST fetch
    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);

    send_request(
      `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
      '',
      {}
    )
      .then(response => {
        if (
          response.data &&
          response.data.components &&
          response.data.components[0].componentData
        ) {
          store.getState().setParseJSONTemplate(response.data.components[0]);
          store.setState({ loaded: true });
          props.onComponentNameChanged(store.getState().name);
          props.onComponentDescriptionChanged(response.data.components[0].description);
          props.onLastModifiedChanged(store.getState().lastModified);
        }
        props.setChildTemplateLoaded(true);
      })
      .catch(err => {
        throw Error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    store.getState().setParseJSONName(componentName);
  }, [componentName]);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      s => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder['selected']) return;
        const { style, key, isNotPlaceholder } = placeholder['selected'];
        insertPlaceholder(style.default, key, style, focusRef.current, isNotPlaceholder);
      }
    );
    return unsub;
  }, []);

  const handleSaveComponent = () => {
    let cData = { ...store.getState().data };
    cData.name = store.getState().name;

    let lastModified = getCurrentTime();
    let data = injectMetadataFields({
      componentData: {
        data: cData,
        name: store.getState().name,
        lastModified: lastModified,
      },
      componentId: props.component_id,
      type: 'parse_json',
      description: store.getState().componentDescription,
    });

    send_component_save_request(
      'component-service/parse_json/data',
      data,
      '',
      'POST'
    )
      .then(response => {
        props.onLastModifiedChanged(lastModified);
        props.showSuccess(data);
        useProjectStore.setState({ savedComponent: true });
      })
      .catch(err => {
        throw err;
      });
  };

  const insertPlaceholder = (label, text, style, currentFocus, isNotPlaceholder) => {
    if (isNotPlaceholder === true) return;
    const newPlaceholders = ['${' + text + '}'];
    store.getState().setPlaceholders(newPlaceholders);
  };

  const cleanJsonString = text => {
    return text.replace(/^["'](.*)["']$/, '$1').replace(/\\"/g, '"');
  };

  const handleUploadSample = async () => {
    setUploadingSample(true);
    try {
      const sampleJson = cleanJsonString(store.getState().data.sample_json);
      JSON.parse(sampleJson);
      await handleJsonFunction.handleAddPlaceholdersJson(sampleJson, store.getState());
      setError(false);
      setJsonParsed(true);
    } catch (err) {
      setError({
        type: 'parse',
        message: `Invalid JSON format: ${err.message}`,
      });
      setJsonParsed(false);
    } finally {
      setUploadingSample(false);
    }
  };

  const handlePrettifyJson = () => {
    try {
      const jsonStr = store.getState().data.sample_json;
      const cleanedJson = cleanJsonString(jsonStr);
      const prettified = JSON.stringify(JSON.parse(cleanedJson), null, 2);
      store.getState().setSampleJSON(prettified);
    } catch (err) {
      setError({
        type: 'prettify',
        message: `Unable to prettify JSON: ${err.message}`,
      });
    }
  };

  const { availablePlaceholders } = props;

  return loading ? (
    <IndividualComponentLoader />
  ) : (
    <CustomPaper
      sx={{
        width: '100%',
        padding: '24px',
      }}
    >
      <Grid container xs={12} direction={'column'}>
        <Grid
          xs={12}
          container
          direction={'column'}
          sx={{
            marginBottom: '1rem',
          }}
        >
          <span
            style={{
              fontSize: '14px',
              marginBottom: '8px',
            }}
          >
            Select the input placeholder containing the JSON string to be parsed
          </span>
          <ChipInput
            inputValue={componentData.placeholders}
            onPlaceholderSelected={props.onPlaceholderSelected}
            placeholders={availablePlaceholders}
            placeholder="Insert a placeholder"
            onBeforeAdd={chip => {
              if (chip.trim() != '') {
                store.getState().setJSONPlaceholder(chip.trim());
                return true;
              }
            }}
            onDelete={() => {
              store.getState().deletePlaceholders();
            }}
            onFocus={() => {}}
          />
        </Grid>
        <Grid
          xs={12}
          container
          direction={'column'}
          sx={{
            marginBottom: '1rem',
          }}
        >
          <StyledGrid container>
            <span style={{ fontSize: '14px' }} xs={10}>
              Copy and paste a sample JSON{' '}
              {componentData.placeholders.length > 0 ? (
                <>
                  of <strong>{componentData.placeholders}</strong>
                </>
              ) : (
                ''
              )}{' '}
              to map to placeholders
            </span>
            <PrettifyButton size="small" onClick={handlePrettifyJson} xs={2}>
              Clean/Prettify
            </PrettifyButton>
          </StyledGrid>
          <Grid item xs={12}>
            <div id="aceditor" style={{ width: '100%' }}>
              <AceEditor
                mode="json"
                theme="github"
                width="100%"
                height="auto"
                minHeight="200px"
                wrapEnabled={true}
                value={componentData.sample_json}
                onBlur={(event, editor) => {
                  store.getState().setSampleJSON(editor.getValue());
                }}
                showPrintMargin={false}
                showGutter={true}
                highlightActiveLine={false}
                fontSize={16}
                name="json-editor"
                editorProps={{
                  $blockScrolling: true,
                  fontSize: '16px',
                  fontFamily: 'JetBrains Mono',
                }}
                setOptions={{
                  enableBasicAutocompletion: false,
                  enableLiveAutocompletion: false,
                  enableSnippets: false,
                  showLineNumbers: true,
                  tabSize: 2,
                  useWorker: false,
                  minLines: 10,
                  maxLines: 50,
                }}
              />
            </div>
          </Grid>
        </Grid>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'stretch',
            marginBottom: 24,
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              handleUploadSample();
            }}
            disabled={uploadingSample}
            sx={{
              width: '100%',
              bgcolor: 'grey.900',
              '&:hover': {
                bgcolor: 'grey.800',
              },
            }}
          >
            {uploadingSample ? (
              <>
                <CircularProgress size={20} sx={{ marginRight: 1, color: 'inherit' }} />
                Parsing...
              </>
            ) : (
              <>
                {jsonParsed ? (
                  <>
                    <RefreshIcon sx={{ marginRight: 1 }} />
                    Parse JSON again
                  </>
                ) : (
                  'Parse JSON'
                )}
              </>
            )}
          </Button>
        </div>
        {error !== false && (
          <p className="err-text err-text-message">
            <ErrorIcon className="iconDiscard" />
            {error.type === 'parse'
              ? error.message
              : error.type === 'prettify'
              ? error.message
              : 'An unexpected error occurred. Please try again.'}
          </p>
        )}
        {!uploadingSample && componentData.samplePlaceholders && componentData.samplePlaceholders.length > 0 && (
          <PlaceholderJson
            componentStore={store.getState()}
            stepStatus={'setPlaceholders'}
            handleKeyChanges={err => {
              setError(err);
              props.handleKeyChanges(err);
            }}
            data={componentData.sample_json}
            placeholderJson={componentData.samplePlaceholders}
          />
        )}
      </Grid>
    </CustomPaper>
  );
};

export default ParseJSONTemplate;
